import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { ComparisonSlider } from './BeforeAfterCamera';
import { sessionService } from '../services/sessionService';
import { getAvailableReactions, canComment, getCommentStyle, hasVerifiedBadge, getRankFrameStyle } from '../utils/rankFeatures';
import { getRankInfo } from '../utils/rankUtils';
import UserProfile from './UserProfile';
import { SafeText, SafeUserContent } from './SafeHTML';
import Modal from './Modal';
import './ActivityFeed.css';

interface ActivityFeedItem {
  id: string;
  userId: string;
  userName: string;
  userRank?: string;
  userAvatar?: string;
  department: string;
  type: 'before-after' | 'video' | 'achievement' | 'rank-up';
  timestamp: string;
  location?: string;
  achievementName?: string;
  newRank?: string;
  points?: number;
  beforeImage?: string;
  afterImage?: string;
  videoUrl?: string;
  caption?: string;
  status?: string;
  sessionId?: string;
  photoPairs?: Array<{
    beforeImage: string;
    afterImage: string;
    orderIndex: number;
  }>;
  reactions: {
    love: string[];
    clap: string[];
    fire: string[];
    star: string[];
    muscle: string[];
    thumbsdown: string[];
  };
  comments: Array<{
    id: string;
    userId: string;
    userName: string;
    content: string;
    timestamp: string;
  }>;
  commentCount?: number;
  hasMoreComments?: boolean;
}

interface ActivityFeedProps {
  onNavigate: (page: string) => void;
}

// Component to display multiple photo pairs
const MultiPhotoDisplay: React.FC<{
  activity: ActivityFeedItem;
  onImageClick?: (image: string) => void;
}> = ({ activity, onImageClick }) => {
  const [photoPairs, setPhotoPairs] = useState<Array<{
    beforeImage: string;
    afterImage: string;
    orderIndex: number;
  }>>([]);
  const [loading, setLoading] = useState(false);
  const [currentPairIndex, setCurrentPairIndex] = useState(0);

  useEffect(() => {
    // Load photo pairs from session if available
    if (activity.sessionId) {
      setLoading(true);
      // Get photo pairs directly from session, not from activity
      sessionService.getSessionPhotoPairs(activity.sessionId).then(pairs => {
        
        if (pairs.length > 0) {
          setPhotoPairs(pairs.map((pair, idx) => ({
            beforeImage: pair.beforePhoto?.storage_url || '',
            afterImage: pair.afterPhoto?.storage_url || '',
            orderIndex: idx
          })));
        } else {
          // Fallback to single photo if no pairs
          setPhotoPairs([{
            beforeImage: activity.beforeImage || '',
            afterImage: activity.afterImage || '',
            orderIndex: 0
          }]);
        }
        setLoading(false);
      }).catch(err => {
        setLoading(false);
      });
    } else if (activity.beforeImage && activity.afterImage) {
      // Single photo pair for backward compatibility
      setPhotoPairs([{
        beforeImage: activity.beforeImage,
        afterImage: activity.afterImage,
        orderIndex: 0
      }]);
    }
  }, [activity.id, activity.sessionId, activity]); // Re-run when activity changes

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem' }}>Loading photos...</div>;
  }

  if (photoPairs.length === 0) {
    return null;
  }

  const currentPair = photoPairs[currentPairIndex];

  return (
    <div className="multi-photo-display">
      <div className="activity-comparison-container" style={{ position: 'relative' }}>
        <ComparisonSlider
          beforeImage={currentPair.beforeImage}
          afterImage={currentPair.afterImage}
          pairNumber={currentPairIndex + 1}
          totalPairs={photoPairs.length}
        />
        
        {photoPairs.length > 1 && (
          <>
            {/* Navigation arrows directly on the image */}
            <button
              onClick={() => setCurrentPairIndex(currentPairIndex === 0 ? photoPairs.length - 1 : currentPairIndex - 1)}
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '20px',
                transition: 'all 0.3s',
                zIndex: 10
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)'}
            >
              ‹
            </button>
            
            <button
              onClick={() => setCurrentPairIndex(currentPairIndex === photoPairs.length - 1 ? 0 : currentPairIndex + 1)}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '20px',
                transition: 'all 0.3s',
                zIndex: 10
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)'}
            >
              ›
            </button>
            
            {/* Pair indicator at the top */}
            <div style={{
              position: 'absolute',
              top: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '6px 16px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '500',
              backdropFilter: 'blur(4px)',
              zIndex: 10
            }}>
              Pair {currentPairIndex + 1} of {photoPairs.length}
            </div>
          </>
        )}
      </div>
      
      {photoPairs.length > 1 && (
        <div style={{ 
          display: 'flex', 
          gap: '8px', 
          justifyContent: 'center',
          marginTop: '12px',
          padding: '8px',
          borderRadius: '20px',
          background: 'rgba(0, 0, 0, 0.05)'
        }}>
          {photoPairs.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentPairIndex(index)}
              style={{
                width: index === currentPairIndex ? '32px' : '12px',
                height: '12px',
                borderRadius: '6px',
                border: 'none',
                background: index === currentPairIndex 
                  ? 'var(--primary)' 
                  : 'rgba(0, 0, 0, 0.2)',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: index === currentPairIndex ? 'scale(1)' : 'scale(0.9)',
                boxShadow: index === currentPairIndex 
                  ? '0 2px 8px rgba(16, 185, 129, 0.3)' 
                  : 'none'
              }}
              aria-label={`Go to pair ${index + 1}`}
              title={`Pair ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ActivityFeed: React.FC<ActivityFeedProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [activities, setActivities] = useState<ActivityFeedItem[]>([]);
  const [filter, setFilter] = useState<'all' | 'department' | 'trending'>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [showCommentInput, setShowCommentInput] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(true);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImage, setModalImage] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [loadingComments, setLoadingComments] = useState<Set<string>>(new Set());
  const [showMenuFor, setShowMenuFor] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userPendingActivities, setUserPendingActivities] = useState<ActivityFeedItem[]>([]);
  const [modalState, setModalState] = useState<{ 
    isOpen: boolean; 
    type: 'success' | 'error' | 'info'; 
    message: string;
    title?: string;
  }>({ isOpen: false, type: 'info', message: '' });
  
  // Confirm modal state for delete confirmations
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ show: false, title: '', message: '', onConfirm: () => {} });
  
  const itemsPerPage = 10;

  // Fetch activities from database
  const fetchActivities = async (pageNum = 0, append = false) => {
    try {
      if (!append) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      // Fetch activities with user profiles and reactions
      let query = supabase
        .from('activities')
        .select(`
          *,
          profile:profiles!activities_user_id_fkey (
            id,
            name,
            department,
            rank,
            avatar_url
          ),
          reactions (
            reaction_type,
            user_id
          )
        `);
      
      // Only show approved activities to everyone
      // Users can see their own activities through a separate query if needed
      query = query.eq('status', 'approved');
      
      const { data: dbActivities, error } = await query
        .order('created_at', { ascending: sortOrder === 'oldest' })
        .range(pageNum * itemsPerPage, (pageNum + 1) * itemsPerPage - 1);

      if (error) throw error;

      // Fetch limited comments for each activity
      const activityIds = dbActivities?.map(a => a.id) || [];
      let commentsData: any[] = [];
      
      if (activityIds.length > 0) {
        // Get first 3 comments for each activity
        const { data: comments } = await supabase
          .from('comments')
          .select(`
            *,
            profiles!comments_profile_id_fkey (
              name
            )
          `)
          .in('activity_id', activityIds)
          .order('created_at', { ascending: false });
        
        commentsData = comments || [];
      }

      // Group comments by activity and count total
      const commentsByActivity: Record<string, any[]> = {};
      const commentCounts: Record<string, number> = {};
      
      commentsData.forEach(comment => {
        if (!commentsByActivity[comment.activity_id]) {
          commentsByActivity[comment.activity_id] = [];
        }
        commentsByActivity[comment.activity_id].push(comment);
      });

      // Get comment counts for activities  
      if (activityIds.length > 0) {
        const { data: countData } = await supabase
          .from('comments')
          .select('activity_id')
          .in('activity_id', activityIds);
        
        countData?.forEach(row => {
          commentCounts[row.activity_id] = (commentCounts[row.activity_id] || 0) + 1;
        });
      }

      // Get only first 3 comments per activity
      Object.keys(commentsByActivity).forEach(activityId => {
        const allComments = commentsByActivity[activityId];
        commentsByActivity[activityId] = allComments.slice(0, 3).reverse(); // Show oldest first of the 3
      });

      // Transform database activities to match the interface
      const transformedActivities: ActivityFeedItem[] = dbActivities?.map(activity => {
        const reactions = {
          love: [] as string[],
          clap: [] as string[],
          fire: [] as string[],
          star: [] as string[],
          muscle: [] as string[],
          thumbsdown: [] as string[]
        };

        // Group reactions by type
        activity.reactions?.forEach((reaction: any) => {
          if (reaction.reaction_type === 'star') {
            reactions.fire.push(reaction.user_id);
          } else if (reactions[reaction.reaction_type as keyof typeof reactions]) {
            reactions[reaction.reaction_type as keyof typeof reactions].push(reaction.user_id);
          }
        });

        return {
          id: activity.id,
          userId: activity.profile_id || activity.user_id,
          userName: activity.profile?.name || 'Unknown User',
          userRank: activity.profile?.rank || 'beginner',
          userAvatar: activity.profile?.avatar_url,
          department: activity.profile?.department || 'Unknown',
          type: activity.activity_type || activity.type,
          timestamp: activity.created_at,
          location: activity.location,
          points: activity.points_awarded || activity.points_earned || 0,
          beforeImage: activity.before_image || activity.before_photo_url,
          afterImage: activity.after_image || activity.after_photo_url,
          videoUrl: activity.video_url,
          caption: activity.description,
          status: activity.status, // Include status for badge display
          sessionId: activity.session_id,
          reactions,
          comments: (commentsByActivity[activity.id] || []).map((comment: any) => ({
            id: comment.id,
            userId: comment.profile_id || '',
            userName: comment.profiles?.name || 'Anonymous',
            content: comment.content,
            timestamp: comment.created_at
          })),
          commentCount: commentCounts[activity.id] || 0,
          hasMoreComments: (commentCounts[activity.id] || 0) > 3
        };
      }) || [];

      // Check if there are more items to load
      setHasMore(dbActivities?.length === itemsPerPage);

      if (append) {
        // Append to existing activities
        setActivities(prev => {
          const newActivities = [...prev, ...transformedActivities];
          // Remove duplicates
          return newActivities.filter((activity, index, self) => 
            index === self.findIndex(a => a.id === activity.id)
          );
        });
      } else {
        // Replace activities
        setActivities(transformedActivities);
      }
    } catch (error) {
      if (!append) {
        // Fallback to localStorage if database fetch fails
        const savedActivities = JSON.parse(localStorage.getItem('campusActivities') || '[]');
        setActivities(savedActivities);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchActivities(nextPage, true);
    }
  };

  const loadAllComments = async (activityId: string) => {
    setLoadingComments(prev => new Set(prev).add(activityId));
    
    try {
      const { data: allComments, error } = await supabase
        .from('comments')
        .select(`
          *,
          profiles!comments_profile_id_fkey (
            name
          )
        `)
        .eq('activity_id', activityId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Update the activity's comments
      setActivities(prev => prev.map(activity => {
        if (activity.id === activityId) {
          return {
            ...activity,
            comments: allComments?.map((comment: any) => ({
              id: comment.id,
              userId: comment.profile_id || '',
              userName: comment.profiles?.name || 'Anonymous',
              content: comment.content,
              timestamp: comment.created_at
            })) || [],
            hasMoreComments: false
          };
        }
        return activity;
      }));

      setExpandedComments(prev => new Set(prev).add(activityId));
    } catch (error) {
    } finally {
      setLoadingComments(prev => {
        const newSet = new Set(prev);
        newSet.delete(activityId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    // Close menu when clicking outside
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't close if clicking inside the menu
      if (target.closest('.activity-menu-container')) {
        return;
      }
      setShowMenuFor(null);
    };

    if (showMenuFor) {
      // Use setTimeout to avoid conflicts with the button click
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 10);
      
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showMenuFor]);

  // Fetch user's pending activities separately
  const fetchUserPendingActivities = async () => {
    if (!user) return;
    
    try {
      const { data: pendingActivities, error } = await supabase
        .from('activities')
        .select(`
          *,
          profile:profiles!activities_user_id_fkey (
            id,
            name,
            department,
            rank,
            avatar_url
          )
        `)
        .eq('profile_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (!error && pendingActivities) {
        const transformed = pendingActivities.map(activity => ({
          id: activity.id,
          userId: activity.profile_id || activity.user_id,
          userName: activity.profile?.name || 'Unknown User',
          userRank: activity.profile?.rank || 'beginner',
          userAvatar: activity.profile?.avatar_url,
          department: activity.profile?.department || 'Unknown',
          type: activity.activity_type || activity.type,
          timestamp: activity.created_at,
          location: activity.location,
          points: activity.points_awarded || activity.points_earned || 0,
          beforeImage: activity.before_image || activity.before_photo_url,
          afterImage: activity.after_image || activity.after_photo_url,
          videoUrl: activity.video_url,
          caption: activity.description,
          status: activity.status,
          sessionId: activity.session_id,
          reactions: { love: [], clap: [], fire: [], star: [], muscle: [], thumbsdown: [] },
          comments: [],
          commentCount: 0,
          hasMoreComments: false
        }));
        setUserPendingActivities(transformed);
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    setPage(0); // Reset page when filter or sort changes
    fetchActivities(0);
    fetchUserPendingActivities();
    
    // Subscribe to new activities and comments
    const subscription = supabase
      .channel('activities_and_comments')
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'activities' 
      }, () => {
        // Only refresh first page to show new activity
        fetchActivities(0);
        fetchUserPendingActivities();
      })
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'comments'
      }, (payload) => {
        // Update the specific activity with the new comment
        if (payload.new && payload.new.activity_id) {
          // Refresh activities to get updated comment counts
          fetchActivities(page, true);
        }
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [sortOrder, filter, user, page]);

  /*
  // Removed sample activities - using real data only
  useEffect(() => {
    const sampleActivities: ActivityFeedItem[] = [
      {
        id: 'sample-1',
        userId: 'user-2',
        userName: 'Sarah Chen',
        userRank: 'eco-warrior',
        department: 'Computer Science',
        type: 'before-after',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        location: 'Library Entrance',
        points: 50,
        reactions: { love: ['user-3', 'user-4'], clap: ['user-5'], fire: [], star: [], muscle: [], thumbsdown: [] },
        comments: [
          {
            id: 'comment-1',
            userId: 'user-3',
            userName: 'Mike Johnson',
            content: 'Great work! The difference is amazing!',
            timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString()
          }
        ]
      },
      // High reaction posts for trending
      {
        id: 'trending-1',
        userId: 'user-10',
        userName: 'Aisha Patel',
        userRank: 'green-champion',
        department: 'Environmental Science',
        type: 'before-after',
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
        location: 'Main Cafeteria',
        points: 100,
        reactions: {
          love: Array(25).fill(0).map((_, i) => `user-${i}`),
          clap: Array(18).fill(0).map((_, i) => `user-${i + 25}`),
          fire: Array(22).fill(0).map((_, i) => `user-${i + 43}`),
          star: Array(10).fill(0).map((_, i) => `user-${i + 65}`),
          muscle: Array(15).fill(0).map((_, i) => `user-${i + 75}`),
          thumbsdown: Array(3).fill(0).map((_, i) => `user-${i + 90}`)
        },
        comments: []
      },
      {
        id: 'trending-2',
        userId: 'user-11',
        userName: 'Carlos Rivera',
        userRank: 'campus-hero',
        department: 'Architecture',
        type: 'video',
        timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
        location: 'Art Building Plaza',
        points: 150,
        reactions: {
          love: Array(30).fill(0).map((_, i) => `user-${i}`),
          clap: Array(28).fill(0).map((_, i) => `user-${i + 30}`),
          fire: Array(25).fill(0).map((_, i) => `user-${i + 58}`),
          star: Array(12).fill(0).map((_, i) => `user-${i + 83}`),
          muscle: Array(20).fill(0).map((_, i) => `user-${i + 95}`),
          thumbsdown: Array(5).fill(0).map((_, i) => `user-${i + 115}`)
        },
        comments: []
      },
      {
        id: 'trending-3',
        userId: 'user-12',
        userName: 'Zara Abdullah',
        userRank: 'legendary',
        department: 'Medicine',
        type: 'before-after',
        timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
        location: 'Hospital Parking Lot',
        points: 200,
        reactions: {
          love: Array(40).fill(0).map((_, i) => `user-${i}`),
          clap: Array(35).fill(0).map((_, i) => `user-${i + 40}`),
          fire: Array(30).fill(0).map((_, i) => `user-${i + 75}`),
          star: Array(15).fill(0).map((_, i) => `user-${i + 105}`),
          muscle: Array(25).fill(0).map((_, i) => `user-${i + 120}`),
          thumbsdown: Array(2).fill(0).map((_, i) => `user-${i + 145}`)
        },
        comments: []
      },
      {
        id: 'sample-2',
        userId: 'user-3',
        userName: 'Mike Johnson',
        userRank: 'cleaner',
        department: 'Engineering',
        type: 'achievement',
        timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
        achievementName: 'Week Warrior',
        points: 10,
        reactions: { love: [], clap: ['user-2'], fire: ['user-4'], star: [], muscle: [], thumbsdown: ['user-9'] },
        comments: []
      },
      {
        id: 'sample-3',
        userId: 'user-4',
        userName: 'Emily Davis',
        department: 'Biology',
        type: 'rank-up',
        timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
        newRank: 'Eco Warrior',
        reactions: { love: ['user-2'], clap: ['user-3'], fire: ['user-5'], star: ['user-8'], muscle: ['user-6'], thumbsdown: [] },
        comments: []
      },
      {
        id: 'sample-4',
        userId: 'user-5',
        userName: 'Alex Kumar',
        department: 'Computer Science',
        type: 'video',
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        location: 'Cafeteria',
        points: 75,
        reactions: { love: ['user-2', 'user-3'], clap: [], fire: ['user-4'], star: [], muscle: [], thumbsdown: [] },
        comments: []
      }
    ];

    // Get saved activities from localStorage
    const savedActivities = JSON.parse(localStorage.getItem('campusActivities') || '[]');
    
    // Combine saved activities with sample activities
    const allActivities = [...savedActivities, ...sampleActivities]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    
    setActivities(allActivities);
  }, []);
  */

  const handleReaction = async (activityId: string, reactionType: keyof ActivityFeedItem['reactions']) => {
    if (!user) return;

    const activity = activities.find(a => a.id === activityId);
    if (!activity) return;

    const hasReacted = activity.reactions[reactionType].includes(user.id);
    
    // Optimistically update UI
    setActivities(prev => prev.map(act => {
      if (act.id === activityId) {
        const newReactions = { ...act.reactions };
        if (hasReacted) {
          newReactions[reactionType] = newReactions[reactionType].filter(id => id !== user.id);
        } else {
          newReactions[reactionType] = [...newReactions[reactionType], user.id];
        }
        return { ...act, reactions: newReactions };
      }
      return act;
    }));

    try {
      // Map reaction types (convert 'muscle' to 'clap' and 'fire' to 'star' for database)
      const dbReactionType = reactionType === 'muscle' ? 'clap' : reactionType === 'fire' ? 'star' : reactionType;
      
      if (hasReacted) {
        // Remove reaction
        await supabase
          .from('reactions')
          .delete()
          .eq('activity_id', activityId)
          .eq('user_id', user.id)
          .eq('reaction_type', dbReactionType);
      } else {
        // Add reaction
        await supabase
          .from('reactions')
          .insert({
            activity_id: activityId,
            user_id: user.id,
            reaction_type: dbReactionType
          });
      }
    } catch (error) {
      // Revert optimistic update on error
      fetchActivities();
    }
  };

  const handleComment = async (activityId: string) => {
    if (!user || !commentText.trim()) return;

    // Store comment text before clearing it
    const commentContent = commentText.trim();
    
    const tempId = Date.now().toString();
    const newComment = {
      id: tempId,
      userId: user.id,
      userName: user.name || 'Anonymous',
      content: commentContent,
      timestamp: new Date().toISOString()
    };

    // Optimistically update UI
    setActivities(prev => prev.map(activity => {
      if (activity.id === activityId) {
        return {
          ...activity,
          comments: [...activity.comments, newComment],
          commentCount: (activity.commentCount || 0) + 1
        };
      }
      return activity;
    }));

    setCommentText('');
    setShowCommentInput(null);

    try {
      // Add comment to database
      const { error } = await supabase
        .from('comments')
        .insert({
          activity_id: activityId,
          profile_id: user.id,
          content: commentContent
        });

      if (error) throw error;
      
      // Refresh to get the real comment ID from database
      fetchActivities(page);
    } catch (error) {
      console.error('Error posting comment:', error);
      // Revert optimistic update on error
      fetchActivities(page);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 1) {
      const minutes = Math.floor(diff / (1000 * 60));
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    }
  };

  const openFullImage = (imageUrl: string) => {
    setModalImage(imageUrl);
    setShowImageModal(true);
  };

  const handleDeleteActivity = (activityId: string) => {
    if (!user) {
      setModalState({ 
        isOpen: true, 
        type: 'error', 
        message: 'You must be logged in to delete posts'
      });
      return;
    }

    setConfirmModal({
      show: true,
      title: 'Delete Post',
      message: 'Are you sure you want to delete this post? This action cannot be undone.',
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, show: false });
        try {
          const { error } = await supabase
            .from('activities')
            .delete()
            .eq('id', activityId)
            .eq('profile_id', user.id);

          if (error) throw error;

          setActivities(prev => prev.filter(activity => activity.id !== activityId));
          
          setModalState({ 
            isOpen: true, 
            type: 'success', 
            message: 'Post deleted successfully',
            title: 'Deleted!'
          });
        } catch (error: any) {
          setModalState({ 
            isOpen: true, 
            type: 'error', 
            message: error?.message || 'Failed to delete post. Please try again.'
          });
        }
      }
    });
  };

  // Delete comment function
  const handleDeleteComment = (activityId: string, commentId: string) => {
    if (!user) return;

    setConfirmModal({
      show: true,
      title: 'Delete Comment',
      message: 'Are you sure you want to delete this comment?',
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, show: false });
        try {
          const { error } = await supabase
            .from('comments')
            .delete()
            .eq('id', commentId)
            .eq('profile_id', user.id);

          if (error) throw error;

          // Remove comment from local state
          setActivities(prev => prev.map(activity => {
            if (activity.id === activityId) {
              return {
                ...activity,
                comments: activity.comments.filter(c => c.id !== commentId),
                commentCount: Math.max(0, (activity.commentCount || 1) - 1)
              };
            }
            return activity;
          }));
        } catch (error: any) {
          setModalState({ 
            isOpen: true, 
            type: 'error', 
            message: 'Failed to delete comment'
          });
        }
      }
    });
  };

  const filteredActivities = activities.filter(activity => {
    if (filter === 'department' && user) {
      return activity.department === user.department;
    }
    if (filter === 'trending') {
      // Calculate total reactions for trending filter
      const totalReactions = (activity.reactions?.love?.length || 0) + 
                           (activity.reactions?.clap?.length || 0) + 
                           (activity.reactions?.fire?.length || 0) + 
                           (activity.reactions?.muscle?.length || 0);
      return totalReactions >= 1; // Show posts with at least 1 reaction
    }
    // In a real app, you'd filter by friends here
    return true;
  });

  const getActivityIcon = (type: ActivityFeedItem['type']) => {
    switch (type) {
      case 'before-after': return '📸';
      case 'video': return '🎬';
      case 'achievement': return '🏆';
      case 'rank-up': return '🎖️';
      default: return '📱';
    }
  };

  const getActivityMessage = (activity: ActivityFeedItem) => {
    switch (activity.type) {
      case 'before-after':
        return <>shared a before/after transformation at <SafeText>{activity.location || 'unknown location'}</SafeText></>;
      case 'video':
        return <>posted a cleaning video from <SafeText>{activity.location || 'unknown location'}</SafeText></>;
      case 'achievement':
        return <>unlocked "<SafeText>{activity.achievementName || 'achievement'}</SafeText>" achievement</>;
      case 'rank-up':
        return <>ranked up to <SafeText>{activity.newRank || 'new rank'}</SafeText></>;
      default:
        return 'shared an activity';
    }
  };

  return (
    <div className="activity-feed-page">
      <div className="feed-header">
        <button onClick={() => onNavigate('dashboard')} className="back-btn">
          ← Back
        </button>
        <h2>Activity Feed</h2>
        <div className="sort-dropdown-container">
          <button 
            className="sort-btn"
            onClick={() => setShowSortDropdown(!showSortDropdown)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M3 7h18M3 12h12M3 17h6" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Sort
          </button>
          {showSortDropdown && (
            <div className="sort-dropdown">
              <button
                className={sortOrder === 'newest' ? 'active' : ''}
                onClick={() => {
                  setSortOrder('newest');
                  setShowSortDropdown(false);
                }}
              >
                Newest First
              </button>
              <button
                className={sortOrder === 'oldest' ? 'active' : ''}
                onClick={() => {
                  setSortOrder('oldest');
                  setShowSortDropdown(false);
                }}
              >
                Oldest First
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="feed-filters">
        <button 
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All Activities
        </button>
        <button 
          className={`filter-btn ${filter === 'department' ? 'active' : ''}`}
          onClick={() => setFilter('department')}
        >
          My Department
        </button>
        <button 
          className={`filter-btn ${filter === 'trending' ? 'active' : ''}`}
          onClick={() => setFilter('trending')}
        >
          Top Activities
        </button>
      </div>

      <div className="feed-content">
        {/* Show user's pending activities first */}
        {userPendingActivities.length > 0 && (
          <div style={{ 
            marginBottom: '2rem',
            padding: '1rem',
            background: 'var(--surface)',
            borderRadius: '0.5rem',
            border: '2px solid #fbbf24'
          }}>
            <h3 style={{ marginBottom: '1rem', color: '#fbbf24' }}>
              Your Pending Activities ({userPendingActivities.length})
            </h3>
            <p style={{ marginBottom: '1rem', fontSize: '0.875rem', opacity: 0.8 }}>
              These activities are awaiting admin approval and are only visible to you.
            </p>
            <div className="activities-list">
              {userPendingActivities.map((activity) => (
                <div key={activity.id} className="activity-card" style={{ borderLeft: '4px solid #fbbf24' }}>
                  {/* Render the same activity card content but for pending activities */}
                  <div className="activity-header">
                    <div className="user-info">
                      <div 
                        className="user-avatar-frame" 
                        style={getRankFrameStyle(activity.userRank || 'beginner')}
                      >
                        {activity.userAvatar ? (
                          <img src={activity.userAvatar} alt={activity.userName} className="user-avatar-img" />
                        ) : (
                          <div className="user-avatar">{activity.userName.charAt(0).toUpperCase()}</div>
                        )}
                      </div>
                      <div>
                        <h4 className="user-name">
                          {activity.userName}
                        </h4>
                        <p><SafeText>{activity.department}</SafeText> • {formatTimestamp(activity.timestamp)}</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{
                        background: '#fbbf24',
                        color: '#000',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '1rem',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        Pending Approval
                      </div>
                    </div>
                  </div>
                  <div className="activity-content">
                    <p>{activity.userName} {getActivityMessage(activity)}</p>
                    {activity.caption && (
                      <p className="activity-caption">{activity.caption}</p>
                    )}
                    {(activity.type === 'before-after' && activity.beforeImage && activity.afterImage) && (
                      <MultiPhotoDisplay 
                        activity={activity} 
                        onImageClick={openFullImage}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {loading ? (
          <div className="loading-state">
            <p>Loading activities...</p>
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="empty-feed">
            <p>{filter === 'trending' ? 'No trending activities yet. React to posts to make them trending!' : 
                filter === 'department' ? 'No activities from your department yet.' : 
                'No activities yet. Be the first to share!'}</p>
            <button onClick={() => onNavigate('camera')} className="cta-btn">
              Start Cleaning
            </button>
          </div>
        ) : (
          <div className="activities-list">
            {(filter === 'trending' 
              ? filteredActivities.sort((a, b) => {
                  const aTotal = (a.reactions?.love?.length || 0) + (a.reactions?.clap?.length || 0) + 
                                (a.reactions?.fire?.length || 0) + (a.reactions?.muscle?.length || 0);
                  const bTotal = (b.reactions?.love?.length || 0) + (b.reactions?.clap?.length || 0) + 
                                (b.reactions?.fire?.length || 0) + (b.reactions?.muscle?.length || 0);
                  return bTotal - aTotal;
                })
              : filteredActivities
            ).map((activity, idx) => (
              <div key={activity.id} className="activity-card">
                <div className="activity-header">
                  <div className="user-info">
                    <div 
                      className="user-avatar-frame clickable" 
                      style={getRankFrameStyle(activity.userRank || 'beginner')}
                      onClick={() => setSelectedUserId(activity.userId)}
                    >
                      {activity.userAvatar ? (
                        <img src={activity.userAvatar} alt={activity.userName} className="user-avatar-img" />
                      ) : (
                        <div className="user-avatar">{activity.userName.charAt(0).toUpperCase()}</div>
                      )}
                    </div>
                    <div>
                      <h4 
                        className="user-name clickable" 
                        onClick={() => setSelectedUserId(activity.userId)}
                      >
                        <SafeText>{activity.userName}</SafeText>
                        {hasVerifiedBadge(activity.userRank || 'beginner') && <span className="verified-badge">✓</span>}
                      </h4>
                      <p><SafeText>{activity.department}</SafeText> • {formatTimestamp(activity.timestamp)}</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {activity.status === 'pending' && (
                      <div style={{
                        background: '#fbbf24',
                        color: '#000',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '1rem',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        Pending Approval
                      </div>
                    )}
                    {activity.status === 'rejected' && (
                      <div style={{
                        background: '#ef4444',
                        color: '#fff',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '1rem',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        Rejected
                      </div>
                    )}
                    {activity.points && activity.status === 'approved' && (
                      <div className="points-badge">+{activity.points} pts</div>
                    )}
                    {user && activity.userId === user.id && (
                      <div className="activity-menu-container">
                        <button
                          className="activity-menu-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowMenuFor(showMenuFor === activity.id ? null : activity.id);
                          }}
                          title="More options"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="5" r="1.5" fill="currentColor"/>
                            <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
                            <circle cx="12" cy="19" r="1.5" fill="currentColor"/>
                          </svg>
                        </button>
                        
                        {showMenuFor === activity.id && (
                          <div className="activity-menu-dropdown">
                            <button
                              className="activity-menu-item delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowMenuFor(null);
                                handleDeleteActivity(activity.id);
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"/>
                              </svg>
                              Delete Post
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="activity-content">
                  <p><SafeText>{activity.userName}</SafeText> {getActivityMessage(activity)}</p>
                  {activity.caption && (
                    <SafeUserContent 
                      content={activity.caption} 
                      className="activity-caption"
                      maxLength={300}
                    />
                  )}
                  
                  {(activity.type === 'before-after' || activity.type === 'video') && (
                    <div className="media-preview">
                      {activity.type === 'before-after' && activity.beforeImage && activity.afterImage ? (
                        <MultiPhotoDisplay 
                          activity={activity} 
                          onImageClick={openFullImage}
                        />
                      ) : activity.type === 'before-after' ? (
                        <div className="before-after-preview">
                          <div className="preview-image">Before</div>
                          <div className="preview-image clean">After</div>
                        </div>
                      ) : activity.videoUrl ? (
                        <div className="video-preview">
                          <video src={activity.videoUrl} controls />
                        </div>
                      ) : (
                        <div className="video-preview">
                          <div className="play-icon">▶️</div>
                          <p>Video</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="activity-actions">
                  <div className="reactions">
                    {(['love', 'clap', 'thumbsdown', 'fire', 'star', 'muscle'] as const).map(reaction => {
                      const emoji = reaction === 'love' ? '❤️' : 
                                   reaction === 'clap' ? '👏' : 
                                   reaction === 'fire' ? '🔥' : 
                                   reaction === 'star' ? '⭐' : 
                                   reaction === 'thumbsdown' ? '👎' : '💪';
                      const count = activity.reactions[reaction].length;
                      const hasReacted = user && activity.reactions[reaction].includes(user.id);
                      const availableReactions = getAvailableReactions(user?.rank || 'beginner');
                      const canUseReaction = availableReactions.includes(reaction) || reaction === 'muscle';
                      
                      if (!canUseReaction && count === 0) return null;
                      
                      return (
                        <button
                          key={reaction}
                          className={`reaction-btn ${hasReacted ? 'active' : ''} ${!canUseReaction ? 'disabled' : ''}`}
                          onClick={() => canUseReaction && handleReaction(activity.id, reaction)}
                          title={!canUseReaction ? `Unlock ${reaction} reaction by ranking up!` : ''}
                        >
                          <span>{emoji}</span>
                          {count > 0 && <span className="count">{count}</span>}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button 
                    className="comment-btn"
                    onClick={() => canComment(user?.rank || 'beginner') ? setShowCommentInput(activity.id === showCommentInput ? null : activity.id) : null}
                    title={!canComment(user?.rank || 'beginner') ? 'Unlock comments by reaching Newcomer rank!' : ''}
                    disabled={!canComment(user?.rank || 'beginner')}
                  >
                    💬 {activity.commentCount || activity.comments.length}
                  </button>
                </div>

                {activity.comments.length > 0 && (
                  <div className="comments-section">
                    {activity.comments.map(comment => (
                      <div key={comment.id} className="comment">
                        <div className="comment-header">
                          <strong><SafeText>{comment.userName}</SafeText></strong>
                          {user && comment.userId === user.id && (
                            <button 
                              className="delete-comment-btn"
                              onClick={() => handleDeleteComment(activity.id, comment.id)}
                              title="Delete comment"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"/>
                              </svg>
                            </button>
                          )}
                        </div>
                        <SafeUserContent 
                          content={comment.content}
                          className={getCommentStyle(user?.rank || 'beginner')}
                          maxLength={500}
                        />
                        <span className="comment-time">{formatTimestamp(comment.timestamp)}</span>
                      </div>
                    ))}
                    
                    {/* View more comments button */}
                    {activity.hasMoreComments && !expandedComments.has(activity.id) && (
                      <button
                        className="view-more-comments-btn"
                        onClick={() => loadAllComments(activity.id)}
                        disabled={loadingComments.has(activity.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#10b981',
                          fontSize: '0.875rem',
                          cursor: loadingComments.has(activity.id) ? 'not-allowed' : 'pointer',
                          padding: '0.5rem 0',
                          fontWeight: '500'
                        }}
                      >
                        {loadingComments.has(activity.id) 
                          ? 'Loading...' 
                          : `View ${(activity.commentCount || 0) - activity.comments.length} more comments`}
                      </button>
                    )}
                  </div>
                )}

                {showCommentInput === activity.id && (
                  <div className="comment-input-section">
                    <input
                      type="text"
                      placeholder="Add a comment..."
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleComment(activity.id)}
                    />
                    <button onClick={() => handleComment(activity.id)}>Post</button>
                  </div>
                )}
              </div>
            ))}
            
            {/* Load More Button */}
            {hasMore && !loading && filteredActivities.length > 0 && (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <button 
                  onClick={loadMore} 
                  className="cta-btn"
                  disabled={loadingMore}
                  style={{ 
                    background: loadingMore ? '#6b7280' : undefined,
                    cursor: loadingMore ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
            
            {!hasMore && filteredActivities.length > 0 && (
              <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>
                <p>No more activities to load</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Image Modal */}
      {showImageModal && (
        <div className="image-modal" onClick={() => setShowImageModal(false)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={modalImage} alt="Full size" />
            <button className="close-modal" onClick={() => setShowImageModal(false)}>×</button>
          </div>
        </div>
      )}
      {selectedUserId && (
        <UserProfile 
          userId={selectedUserId}
          onNavigate={onNavigate}
          onClose={() => setSelectedUserId(null)}
        />
      )}
      
      <Modal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        type={modalState.type}
        title={modalState.title}
        message={modalState.message}
      />

      {/* Confirm Modal for delete actions */}
      {confirmModal.show && (
        <div className="confirm-modal-overlay" onClick={() => setConfirmModal({ ...confirmModal, show: false })}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{confirmModal.title}</h3>
            <p>{confirmModal.message}</p>
            <div className="confirm-modal-actions">
              <button 
                className="confirm-cancel-btn"
                onClick={() => setConfirmModal({ ...confirmModal, show: false })}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn"
                onClick={confirmModal.onConfirm}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;